import { EmeraldSearch } from "@emerald-react/search";
import type { AllUser } from "@shared/types/user.types";
import { useState } from "react";

interface UserSearchDropdownProps {
  users: AllUser[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onUserSelect: (user: AllUser) => Promise<void>;
}

const UserSearchDropdown = ({
  users,
  searchValue,
  onSearchChange,
  onUserSelect,
}: UserSearchDropdownProps) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Filter users based on search input
  const filteredUsers = !searchValue.trim()
    ? []
    : (users || []).filter(
        (user) =>
          user.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchValue.toLowerCase()),
      );

  const handleSearchChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    onSearchChange(value);
    setShowDropdown(value.trim().length > 0);
  };

  const handleUserSelect = (user: AllUser) => {
    onUserSelect(user);
    setShowDropdown(false);
  };

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleFocus = () => {
    setShowDropdown(searchValue.trim().length > 0);
  };

  const shouldShowDropdown = showDropdown && searchValue.trim().length > 0;

  return (
    <div className="relative mb-3">
      <div className="flex gap-2">
        <EmeraldSearch
          placeholder="Add a Site IQ user"
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className="flex-1"
        />
      </div>

      {/* User Dropdown */}
      {shouldShowDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => handleUserSelect(user)}
              >
                <div className="font-medium text-sm">{user.name}</div>
                <div className="text-xs text-gray-600">{user.email}</div>
              </div>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              Sorry, no users found matching &quot;{searchValue}&quot;.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchDropdown;
